import { Box, Button, IconButton, Typography } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useLocation } from "react-router-dom";
import API from "../../../api";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Globe from "react-globe.gl";
import CompactDateFilter from "../../../components/CompactDateFilter";
import CloseIcon from "@mui/icons-material/Close";
import { IpPanel } from "./IpPanel";

const compactTableStyle = {
  height: "100%",
  width: "100%",
  border: "none",
  borderRadius: 0,
  "& .MuiDataGrid-toolbar": { backgroundColor: "transparent" },
  "& .MuiToolbar-root": {
    gap: 1,
    backgroundColor: "transparent",
    borderBottom: "1px solid rgba(68,144,149,0.2)",
    minHeight: "34px",
    padding: "0 8px",
  },
  "& .MuiToolbar-root .MuiButton-root": {
    color: "#aaa",
    fontSize: "0.65rem",
    padding: "2px 4px",
    minWidth: 0,
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "transparent",
    borderBottom: "1px solid rgba(68,144,149,0.35)",
    padding: "0 8px",
    color: "#449095",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.68rem",
  },
  "& .MuiDataGrid-cell": {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgba(68,144,149,0.1)",
    padding: "0 8px",
    fontSize: "0.78rem",
    color: "#eaeaea",
  },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": { display: "none" },
  "& .MuiDataGrid-row": {
    minHeight: "36px !important",
    cursor: "pointer",
    "&:hover .MuiDataGrid-cell": { backgroundColor: "rgba(68,144,149,0.09)" },
  },
  "& .MuiDataGrid-selectedRowCount": { display: "none" },
};

const Map = ({ customerName }) => {
  const globeEl = useRef();
  const location = useLocation();

  const [countries, setCountries] = useState({ features: [] });
  const [hoverD, setHoverD] = useState();
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [nameCountryS, setNameCountryS] = useState("Select a Country");
  const [speedRotate, setSpeedRotate] = useState(10);
  const [charged, setCharged] = useState(true);
  const [dateFilter, setDateFilter] = useState({ dateFrom: null, dateTo: null });

  const [selectedIp, setSelectedIp] = useState(null);
  const [ipData, setIpData] = useState(null);
  const [ipDataCharged, setIpDataCharged] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const buildParams = useCallback((filters) => {
    const params = {};
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, []);

  const getPolygonColor = useCallback(
    (feature) => {
      return feature === hoverD
        ? "rgba(255, 120, 120, 0.95)"
        : "rgba(220, 30, 30, 0.75)";
    },
    [hoverD]
  );

  const getPolygonAltitude = useCallback(
    (feature) => {
      if (feature === hoverD) return 0.15;
      return 0.05;
    },
    [hoverD]
  );

  const normalizeRows = useCallback((data = [], type, countryCode) => {
    return data.map((item, index) => {
      const ip =
        item?.ip ||
        item?._id?.ip ||
        item?._id?.sourceIP ||
        item?._id?.destinationIP ||
        item?._id ||
        "";
      return {
        id: `${type}-${ip || countryCode || "row"}-${index}`,
        ip,
        countIP: item?.countIP || item?.count || 0,
        origin: type === "map" ? "lecs" : "agent",
        rawSource: type,
        ...item,
      };
    });
  }, []);

  const loadCountryDetail = useCallback(
    async (countryCode, countryName, source, filters = dateFilter) => {
      try {
        setCharged(false);
        setNameCountryS(countryName || "Select a Country");
        setSelectedCountry([]);
        const params = { iso: countryCode, ...buildParams(filters) };
        const response = await API.getMap(params);
        setSelectedCountry(normalizeRows(response.data || [], "map", countryCode));
      } catch (error) {
        console.error("[loadCountryDetail] Errore nel caricamento del dettaglio paese:", error);
        setSelectedCountry([]);
      } finally {
        setCharged(true);
      }
    },
    [buildParams, dateFilter, normalizeRows]
  );

  const loadMapData = useCallback(
    async (filters = dateFilter) => {
      try {
        setCharged(false);
        const params = buildParams(filters);
        const [mapResult, geoJsonResponse] = await Promise.all([
          API.getMap(params),
          fetch("/Country.geojson"),
        ]);
        const geoJson = await geoJsonResponse.json();
        const mapByCountryCode = (mapResult.data || []).reduce((acc, item) => {
          const countryCode = item?._id?.countryCode;
          if (!countryCode) return acc;
          acc[countryCode] = { countIP: item.countIP || 0 };
          return acc;
        }, {});
        const filteredCountries = (geoJson?.features || [])
          .filter((feature) => {
            const isoCode = feature?.properties?.ISO_A2;
            return !!isoCode && Object.prototype.hasOwnProperty.call(mapByCountryCode, isoCode);
          })
          .map((feature) => {
            const isoCode = feature.properties.ISO_A2;
            const hasMap = Object.prototype.hasOwnProperty.call(mapByCountryCode, isoCode);
            return {
              ...feature,
              properties: {
                ...feature.properties,
                mapCountIP: hasMap ? mapByCountryCode[isoCode].countIP : 0,
                source: "map",
              },
            };
          });
        setCountries({ features: filteredCountries });
      } catch (error) {
        console.error("[loadMapData] Errore nel caricamento dei dati della mappa:", error);
        setCountries({ features: [] });
      } finally {
        setCharged(true);
      }
    },
    [buildParams, dateFilter]
  );

  const handleDateChange = async (newDateFilter) => {
    setDateFilter(newDateFilter);
    await loadMapData(newDateFilter);
    if (nameCountryS !== "Select a Country") {
      const currentCountry = countries.features.find(
        (feature) => feature.properties.ADMIN === nameCountryS
      );
      if (currentCountry) {
        await loadCountryDetail(
          currentCountry.properties.ISO_A2,
          currentCountry.properties.ADMIN,
          currentCountry.properties.source,
          newDateFilter
        );
      }
    }
  };

  // Fetch IP inspection data when an IP is selected
  useEffect(() => {
    if (!selectedIp) return;
    setIpData(null);
    setIpDataCharged(false);
    API.getIpInspection({ ip: selectedIp })
      .then((res) => { setIpData(res.data); setIpDataCharged(true); })
      .catch((err) => { console.error('[Map] failed to fetch IP inspection data:', err); setIpDataCharged(true); });
  }, [selectedIp]);

  useEffect(() => {
    if (!customerName && window.sessionStorage.getItem("type") === "reseller") {
      window.sessionStorage.setItem("selectedTab", "Dashboard");
      const checkCondition = () => {
        if (window.sessionStorage.getItem("selectedTab") === "Dashboard") {
          window.location.href = `/${window.sessionStorage.getItem("type")}/home`;
        } else {
          setTimeout(checkCondition, 500);
        }
      };
      setTimeout(checkCondition, 0);
    }
  }, [customerName]);

  useEffect(() => {
    const homeCountry = queryParams.get("country");
    const countryCode = queryParams.get("countryCode");
    const ipFromQuery = queryParams.get("ip");
    if (homeCountry && countryCode) {
      setNameCountryS(homeCountry);
    } else {
      setNameCountryS("Select a Country");
      setSelectedCountry([]);
      setCharged(true);
    }
    if (ipFromQuery) setSelectedIp(ipFromQuery);
  }, [queryParams]);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    main.scrollTo(0, 0);
    const originalOverflow = main.style.overflow;
    main.style.overflow = "hidden";
    loadMapData();
    setSpeedRotate(0.25);
    return () => { main.style.overflow = originalOverflow; };
  }, [loadMapData]);

  useEffect(() => {
    if (!globeEl.current) return;
    const controls = globeEl.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = speedRotate;
    const camera = controls.object;
    camera.position.set(0, 0, 650);
  }, [speedRotate, countries]);

  useEffect(() => {
    const homeCountry = queryParams.get("country");
    const countryCode = queryParams.get("countryCode");
    if (!homeCountry || !countryCode || countries.features.length === 0) return;
    const selectedFeature = countries.features.find(
      (feature) =>
        feature.properties.ISO_A2 === countryCode ||
        feature.properties.ADMIN === homeCountry
    );
    if (selectedFeature) {
      loadCountryDetail(
        selectedFeature.properties.ISO_A2,
        selectedFeature.properties.ADMIN,
        selectedFeature.properties.source,
        dateFilter
      );
    }
  }, [countries, dateFilter, loadCountryDetail, queryParams]);

  const handleSelect = async (polygon) => {
    setSelectedIp(null);
    await loadCountryDetail(
      polygon.properties.ISO_A2,
      polygon.properties.ADMIN,
      polygon.properties.source,
      dateFilter
    );
  };

  const handleRowClick = (params) => {
    const ip = params?.row?.ip;
    if (ip) setSelectedIp(ip);
  };

  const columns = [
    {
      field: "origin",
      headerName: "Origin",
      minWidth: 70,
      flex: 0.6,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box
          sx={{
            fontWeight: 600,
            fontSize: "0.7rem",
            color: params.value === "lecs" ? "#ff8a80" : "#82b1ff",
            textTransform: "uppercase",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "ip",
      headerName: "IP",
      minWidth: 110,
      flex: 1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "countIP",
      headerName: "Count",
      minWidth: 50,
      flex: 0.5,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "info",
      headerName: "",
      minWidth: 70,
      flex: 0.55,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: () => (
        <Button
          size="small"
          sx={{
            backgroundColor: "#2a2a2a",
            color: "#ccc",
            fontSize: "0.65rem",
            px: 1,
            py: 0.25,
            minWidth: 0,
            fontWeight: 400,
            lineHeight: 1.4,
            "&:hover": { backgroundColor: "#449095 !important", color: "#fff" },
          }}
        >
          Inspect
        </Button>
      ),
    },
  ];

  return (
    <Box overflow="hidden" position="relative" height="100vh" width="100%">
      {/* Globe — full screen */}
      <Box className="mainGlobe">
        <Globe
          ref={globeEl}
          globeImageUrl="/earth-blue-marble.jpg"
          backgroundImageUrl="/night-sky.png"
          bumpImageUrl="/earth-topology.png"
          polygonsData={countries.features}
          polygonAltitude={getPolygonAltitude}
          polygonCapColor={getPolygonColor}
          polygonSideColor={() => "rgba(120, 0, 0, 0.35)"}
          polygonStrokeColor={() => "rgba(255, 120, 120, 0.7)"}
          polygonLabel={({ properties: d }) => `
            <div style="
              background-color: #162342;
              border-radius: 4px;
              padding: 8px;
              font-size: 12px;
              color: #fff;
              text-align: left;
              min-width: 140px;
            ">
              <b>${d.ADMIN}</b><br />
              Lecs IP: <i>${d.mapCountIP || 0}</i><br />
            </div>
          `}
          polygonsTransitionDuration={1000}
          onPolygonHover={setHoverD}
          onPolygonClick={handleSelect}
        />
      </Box>

      {/* Left column: filter + country table */}
      <Box
        position="absolute"
        top={10}
        left={10}
        zIndex={1000}
        sx={{ width: { xs: "calc(100% - 20px)", md: "44%", lg: "29%" } }}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <CompactDateFilter
          inline={true}
          title="Time Filter"
          onDateChange={handleDateChange}
          initialDateFrom={dateFilter.dateFrom}
          initialDateTo={dateFilter.dateTo}
        />

        <Box
          sx={{
            background: "rgba(0, 0, 0, 0.82)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
            border: "1px solid #449095",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "48vh",
          }}
        >
          <Box px={1.5} py={0.75} borderBottom="1px solid rgba(68,144,149,0.2)">
            <Typography variant="body2" color="#eaeaea" fontWeight={600} noWrap>
              {nameCountryS}
            </Typography>
          </Box>
          <Box flex={1} minHeight={0} overflow="hidden">
            {selectedCountry.length === 0 ? (
              <Typography variant="body2" color="#eaeaea" noWrap p={2}>
                {charged ? "Select a country on the globe to see details" : "Loading..."}
              </Typography>
            ) : (
              <DataGrid
                density="compact"
                className="globeTable"
                rows={selectedCountry}
                columns={columns}
                hideFooter
                style={{ height: "100%", width: "100%" }}
                slots={{ toolbar: GridToolbar }}
                onRowClick={handleRowClick}
                sx={compactTableStyle}
                slotProps={{
                  toolbar: { printOptions: { disableToolbarButton: true } },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Right panel: IP Inspection */}
      {selectedIp && (
        <Box
          position="absolute"
          top={10}
          right={10}
          zIndex={1000}
          sx={{
            width: { xs: "calc(100% - 20px)", md: "44%", lg: "27%" },
            maxHeight: "calc(100vh - 20px)",
            overflowY: "auto",
            background: "rgba(0, 0, 0, 0.88)",
            backdropFilter: "blur(12px)",
            borderRadius: "15px",
            border: "1px solid #449095",
          }}
          className="highlights"
        >
          {/* Sticky header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={1.5}
            py={0.75}
            position="sticky"
            top={0}
            bgcolor="rgba(0,0,0,0.92)"
            zIndex={2}
          >
            <Typography
              variant="body1"
              color="#449095"
              fontWeight={700}
              sx={{ fontFamily: "monospace", letterSpacing: 0.5 }}
            >
              Ip details
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSelectedIp(null)}
              sx={{ color: "#eaeaea", p: 0.5, "&:hover": { color: "#449095" } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <IpPanel data={ipData} charged={ipDataCharged} />
        </Box>
      )}
    </Box>
  );
};

export default Map;
